import type { AiCall, AiChoice, AiHistoryTurnDto, AiPlanEntry, AiSuggestion } from '@/entities/ai-assistant';

/** Один вопрос и ответ ассистента. Серверной истории нет (в БД ничего не пишем): беседа хранится в браузере. */
export interface ChatEntry {
  id: string;
  /** Диалог: контекст для модели берётся только из записей текущего диалога («Новый диалог» его меняет). */
  sessionId: string;
  question: string;
  reply: string;
  answered: boolean;
  calls: AiCall[];
  warnings: string[];
  /** План вызовов от сервера (режим `intent`): уходит обратно как контекст следующего вопроса. */
  plan?: AiPlanEntry[];
  /** Варианты и кнопки следующего шага (режим `intent`); показываются только под последним ответом. */
  choices?: AiChoice[];
  suggestions?: AiSuggestion[];
  /** Ответ получен с данными отчётов (кнопка «Разобрать подробнее»). */
  detailed?: boolean;
  /** ISO-время ответа. */
  at: string;
}

/** Сколько записей помнит браузер: таблицы в ответах тяжёлые, а `localStorage` — около 5 МБ. */
export const MAX_STORED_ENTRIES = 30;
/** Сколько последних реплик текущего диалога уходит модели для контекста («а за вчера?») — столько же принимает сервер. */
export const MAX_CONTEXT_TURNS = 6;

export const historyKey = (storeId: string, userId: string): string => `ai:history:${storeId}:${userId}`;

function isEntry(value: unknown): value is ChatEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<ChatEntry>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.sessionId === 'string' &&
    typeof entry.question === 'string' &&
    typeof entry.reply === 'string' &&
    typeof entry.answered === 'boolean' &&
    Array.isArray(entry.calls) &&
    Array.isArray(entry.warnings) &&
    typeof entry.at === 'string'
  );
}

/** Прочитанная история; при пустом, повреждённом или недоступном хранилище (приватное окно) — пустой список. */
export function loadHistory(storeId: string, userId: string): ChatEntry[] {
  try {
    const raw = window.localStorage.getItem(historyKey(storeId, userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry).slice(-MAX_STORED_ENTRIES) : [];
  } catch {
    return [];
  }
}

/**
 * Сохраняет последние записи. Если хранилище переполнено — выбрасывает самые старые, пока не
 * поместится; если недоступно совсем, молча остаётся без истории (чат продолжает работать).
 */
export function saveHistory(storeId: string, userId: string, entries: ChatEntry[]): void {
  let kept = entries.slice(-MAX_STORED_ENTRIES);
  while (true) {
    try {
      window.localStorage.setItem(historyKey(storeId, userId), JSON.stringify(kept));
      return;
    } catch {
      if (kept.length <= 1) return;
      kept = kept.slice(Math.ceil(kept.length / 4));
    }
  }
}

export function clearHistory(storeId: string, userId: string): void {
  try {
    window.localStorage.removeItem(historyKey(storeId, userId));
  } catch {
    // Хранилище недоступно — нечего очищать.
  }
}

/** Реплики текущего диалога для контекста модели: текст вопроса и ответа и план вызовов (если есть), без таблиц. */
export function contextOf(entries: readonly ChatEntry[], sessionId: string): AiHistoryTurnDto[] {
  return entries
    .filter((entry) => entry.sessionId === sessionId && entry.reply.trim() !== '')
    .slice(-MAX_CONTEXT_TURNS)
    .map(({ question, reply, plan }) => ({ question, reply, ...(plan && plan.length > 0 ? { plan } : {}) }));
}
